import { PrismicPdpJson, getGroup, getPdp, retry } from "@jog/shared"
import { PdpGroup, PdpProduct, ProductPage } from "@jog/types"
import { useRouter } from "next/router"
import {
    Dispatch,
    PropsWithChildren,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react"

import { getItemData } from "./useProductDetail"

interface PdpContext {
    pdp: PrismicPdpJson
    detail: ProductPage["productDetailInfo"]
    sku: string
    setSku: Dispatch<SetStateAction<string>>
    productData: PdpProduct | null
    pending: boolean
    variants: PdpGroup[]
    fasletAvailable: boolean
    setFasletAvailable: Dispatch<SetStateAction<boolean>>
}
const PdpContext = createContext<PdpContext>({} as PdpContext)

export const usePdp = () => useContext(PdpContext)!

export const PdpProvider = ({
    children,
    pdp,
    detail,
}: PropsWithChildren<{ pdp: PrismicPdpJson; detail: ProductPage["productDetailInfo"] }>) => {
    const [sku, setSku] = useState("")
    const [productData, setProductData] = useState<PdpProduct | null>(null)
    const [pending, setPending] = useState(true)
    const [variants, setVariants] = useState<PdpGroup[]>([])
    const router = useRouter()
    const fetchPdpData = useCallback(async () => {
        setPending(true)
        const res = await getPdp(detail.ctpId).catch(() => null)
        if (!res) {
            const qs = new URLSearchParams()
            qs.set("redirect", "true")
            await router.push(`/${detail.productCategoryPath}?${qs}`)
            return
        }
        setProductData(res)
        setPending(false)
    }, [detail.ctpId, detail.productCategoryPath, router])
    const fetchVariants = useCallback(async () => {
        const parent = detail.parent
        if (!parent || parent === "-") return
        await retry(() => getGroup(parent))
            .then((res) => res?.[parent]?.map((item) => getItemData(item, pdp.variantItemCover)) || [])
            .then((res) => setVariants(res))
    }, [detail.parent, pdp.variantItemCover])
    useEffect(() => {
        if (router.isReady) {
            Promise.all([fetchVariants(), fetchPdpData()]).catch(() => {
                router.push("/404").catch(() => {})
            })
        }
    }, [fetchPdpData, fetchVariants, router, router.isReady])
    const [fasletAvailable, setFasletAvailable] = useState(false)

    return (
        <PdpContext.Provider
            value={{ pdp, detail, sku, setSku, pending, variants, productData, fasletAvailable, setFasletAvailable }}>
            {children}
        </PdpContext.Provider>
    )
}
